import { clsx } from "clsx";
import React, { useState } from "react";
import type { GetServerDataProps } from "gatsby";

import {
  Link,
  IconUser,
  IconHome,
  IconOrder,
  IconLogout,
  IconHeartSmall,
  Container,
  Accordion,
  OrdersCard,
  LogoutCard,
  Breadcrumbs,
  AddressCard,
  useBreakpoint,
  MetaComponent,
  AccordionSection,
  CustomerDetailsCard,
  EmailPreferencesCard,
  type EmailPreferencesCardProps,
} from "@bared/ui";

import { pageAccountDashboardQuery, getCountriesWithRegions } from "@bared/lib";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { ErrorPage } from "~/components/Error/Error";

import { seoConfigForPage, getSeoMeta } from "~/lib/seo";
import { contextFromServerDataProps } from "~/lib/server";

import { useCustomerLogout } from "~/hooks/useCustomer";

import { withAuthGuard } from "~/hocs/withAuthGuard";

import { WishlistCard } from "./WishlistCard";

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const { respond, sanity, notFound, site } = context;

  // Resolve handle from list of allowed sections.
  const handle =
    "string" === typeof props.params?.["handle"] &&
    ["details", "addresses", "orders", "wishlist", "logout"].includes(
      props.params["handle"],
    )
      ? props.params["handle"]
      : "details";

  // Resolve base path for accordion.
  const basePath = "/" + site.handle + "/account";

  // Fetch content from sanity.
  const pageAccountDashboard = await sanity.fetch(pageAccountDashboardQuery);

  // Content not found.
  if (!pageAccountDashboard) return notFound();

  // Build a list of interest options for email preferences card.
  const interestOptions: EmailPreferencesCardProps["interestOptions"] = (
    pageAccountDashboard.preferencesList ?? []
  ).map(({ label, value }) => ({ label, value }));

  // Build a list of frequency options for email preferences card.
  const frequencyOptions: EmailPreferencesCardProps["frequencyOptions"] = [
    { value: "All", label: "All the emails!" },
    { value: "Weekly", label: "Once per week, please" },
  ];

  // Resolve countries with regions for address selector.
  const countriesWithRegions = getCountriesWithRegions();

  // Resolve seo config.
  const seo = seoConfigForPage({ context });

  // Respond with server data.
  return respond({
    seo,
    handle,
    basePath,
    interestOptions,
    frequencyOptions,
    countriesWithRegions,
  } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

function Page({ serverData }: PageProps<ServerData>) {
  const [portalNode, setPortalNode] = useState<HTMLDivElement | null>(null);

  const isSmall = useBreakpoint("sm");

  const { logoutCustomer } = useCustomerLogout();

  /**
   * Scroll to the section with the given ID.
   *
   * This is used to scroll to the top of an accordion section each time one of the internal
   * "pages" of a form changes. This prevents the scroll being affected by the page height
   * changing as the internal content of the forms changes.
   *
   * On mobile, it scrolls to the accordion section, while on desktop, it scrolls to the
   * top of the page, because desktop uses a portal.
   */
  function scrollToSection(sectionId: string) {
    if (isSmall) {
      const section = document.getElementById(`section-${sectionId}`);

      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  // Page found, render page.
  if (200 === serverData.status) {
    const {
      handle,
      basePath,
      interestOptions,
      frequencyOptions,
      countriesWithRegions,
    } = serverData;

    return (
      <div className="bg-neutral">
        <Container maxWidth="xl">
          <Breadcrumbs title="My account" className="py-midi">
            <Link href="/" variant="link">
              Home
            </Link>
          </Breadcrumbs>
          <div
            className={clsx(!isSmall && "grid grid-cols-12 gap-mega pb-mega")}
          >
            <div className={clsx(!isSmall && "col-span-4")}>
              {(isSmall || portalNode) && (
                <Accordion
                  defaultOpenSections={[handle]}
                  {...(portalNode
                    ? { portalNode, syncUrl: { strategy: "path", basePath } }
                    : {})}
                >
                  <AccordionSection
                    title="My Details"
                    id="details"
                    key="details"
                    htmlId="section-details"
                    icon={<IconUser className="w-[32px] text-primary" />}
                  >
                    <div className="flex flex-col gap-maxi">
                      <CustomerDetailsCard
                        onPageChange={() => scrollToSection("details")}
                      />
                      <EmailPreferencesCard
                        interestOptions={interestOptions}
                        frequencyOptions={frequencyOptions}
                      />
                    </div>
                  </AccordionSection>

                  <AccordionSection
                    title="My Addresses"
                    id="addresses"
                    key="addresses"
                    htmlId="section-addresses"
                    icon={<IconHome className="w-[32px] text-primary" />}
                  >
                    <AddressCard
                      countriesWithRegions={countriesWithRegions}
                      onPageChange={() => scrollToSection("addresses")}
                    />
                  </AccordionSection>

                  <AccordionSection
                    title="Order History"
                    id="orders"
                    key="orders"
                    icon={<IconOrder className="w-[32px] text-primary" />}
                  >
                    <OrdersCard />
                  </AccordionSection>

                  <AccordionSection
                    title="My Wishlist"
                    id="wishlist"
                    key="wishlist"
                    icon={<IconHeartSmall className="w-[32px] text-primary" />}
                  >
                    <WishlistCard />
                  </AccordionSection>

                  <AccordionSection
                    title="Logout"
                    id="logout"
                    key="logout"
                    icon={<IconLogout className="w-[32px] text-primary" />}
                  >
                    <LogoutCard onConfirm={() => logoutCustomer()} />
                  </AccordionSection>
                </Accordion>
              )}
            </div>
            {!isSmall && <div className="col-span-8" ref={setPortalNode} />}
          </div>
        </Container>
      </div>
    );
  }

  return null;
}

export default withAuthGuard(Page);

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;
  return (
    <MetaComponent
      meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
    />
  );
}
